/**
 * splitHtmlForAds — cut an article's HTML string into segments so in-article
 * ad units can be rendered between them (TopicalGuidePage).
 *
 * Cuts are made only at TOP-LEVEL block boundaries, never inside a list,
 * table, figure or wrapper <div>, so every segment is balanced HTML and the
 * markup (ids, anchors, attributes) is unchanged; joining the segments
 * gives back the input exactly.
 *
 * Rules:
 *   - 1st cut after the `firstAfterP`-th top-level </p> (default 3rd).
 *   - 2nd cut at least `gap` top-level blocks after the 1st (default 6).
 *   - A cut always follows a <p> and is followed by a <p> or a heading
 *     without links (never a list, table, figure, <hr>, wrapper, or a
 *     "Download the app: CLICK HERE" style heading), with at least
 *     `minAfter` top-level blocks still to come, so no unit sits at the end.
 *   - At most `max` cuts (default 2). Short articles get 1 or 0.
 *   - Unbalanced markup (a stray close tag, or WordPress leftovers like a
 *     trailing unclosed <p>): everything from the top-level element where
 *     it starts to the end is one opaque '#rest' block, never cut into.
 *
 * Pure function: no DOM needed, so it runs the same in jest and browsers.
 */

const VOID = new Set([
  'area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input',
  'link', 'meta', 'param', 'source', 'track', 'wbr',
]);
const RAW_TEXT = new Set(['script', 'style', 'textarea', 'title']);
const AFTER_OK = new Set(['p', 'h2', 'h3', 'h4', 'h5', 'h6']);

// <tag attr="v" attr='v' attr=v attr> | </tag> | <!-- comment --> | <!doctype>
const TOKEN = /<!--[\s\S]*?-->|<!\w[^>]*>|<(\/?)([a-zA-Z][\w:-]*)((?:\s+[^\s"'>/=]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s"'>]+))?)*)\s*(\/?)>/g;

/** Top-level blocks as [{ tag, start, end, hasLink }] (end = index just
 *  after the block). Non-blank top-level text counts as a '#text' block;
 *  an unbalanced tail becomes a single '#rest' block. */
export function topLevelBlocks(html) {
  let prev = 0;
  return scanBlocks(html).map((b) => {
    const block = { ...b, start: prev, hasLink: /<a[\s>]/i.test(html.slice(prev, b.end)) };
    prev = b.end;
    return block;
  });
}

function scanBlocks(html) {
  const blocks = [];
  const stack = [];
  let last = 0;
  let topStart = 0; // where the current top-level element opened
  const rest = () => {
    if (html.slice(topStart).trim()) blocks.push({ tag: '#rest', end: html.length });
    return blocks;
  };
  TOKEN.lastIndex = 0;
  let m;
  while ((m = TOKEN.exec(html)) !== null) {
    if (stack.length === 0 && html.slice(last, m.index).trim()) {
      blocks.push({ tag: '#text', end: m.index });
    }
    last = TOKEN.lastIndex;
    const [, closing, rawName, , selfClose] = m;
    if (!rawName) continue; // comment / doctype
    const name = rawName.toLowerCase();
    if (closing) {
      if (stack[stack.length - 1] !== name) return rest(); // unbalanced
      stack.pop();
      if (stack.length === 0) blocks.push({ tag: name, end: TOKEN.lastIndex });
    } else if (VOID.has(name) || selfClose) {
      if (stack.length === 0) blocks.push({ tag: name, end: TOKEN.lastIndex });
    } else if (RAW_TEXT.has(name)) {
      if (stack.length === 0) topStart = m.index;
      const close = html.toLowerCase().indexOf(`</${name}`, TOKEN.lastIndex);
      const gt = close === -1 ? -1 : html.indexOf('>', close);
      if (gt === -1) return rest();
      TOKEN.lastIndex = gt + 1;
      last = TOKEN.lastIndex;
      if (stack.length === 0) blocks.push({ tag: name, end: TOKEN.lastIndex });
    } else {
      if (stack.length === 0) topStart = m.index;
      stack.push(name);
    }
  }
  if (stack.length) return rest();
  if (html.slice(last).trim()) blocks.push({ tag: '#text', end: html.length });
  return blocks;
}

/** Indexes (into topLevelBlocks) after which to cut. */
export function adCutPoints(blocks, { firstAfterP = 3, gap = 6, minAfter = 3, max = 2 } = {}) {
  const cuts = [];
  let pSeen = 0;
  for (let i = 0; i < blocks.length && cuts.length < max; i += 1) {
    if (blocks[i].tag === 'p') pSeen += 1;
    const canCut =
      blocks[i].tag === 'p' &&
      pSeen >= firstAfterP &&
      blocks.length - 1 - i >= minAfter &&
      AFTER_OK.has(blocks[i + 1]?.tag) &&
      (blocks[i + 1].tag === 'p' || !blocks[i + 1].hasLink) &&
      (cuts.length === 0 || i - cuts[cuts.length - 1] >= gap);
    if (canCut) cuts.push(i);
  }
  return cuts;
}

/** Split `html` into 1..max+1 segments; an ad goes between each pair. */
export default function splitHtmlForAds(html, opts) {
  if (typeof html !== 'string' || !html) return [html || ''];
  const blocks = topLevelBlocks(html);
  const cuts = adCutPoints(blocks, opts);
  if (!cuts.length) return [html];
  const segments = [];
  let start = 0;
  cuts.forEach((i) => {
    segments.push(html.slice(start, blocks[i].end));
    start = blocks[i].end;
  });
  segments.push(html.slice(start));
  return segments;
}

// Link text that makes a link a call to action ("Download the app: CLICK
// HERE"), as opposed to an ordinary in-prose reference link.
const CTA_LINK = /click here|download|get the app|install|book now|buy now|sign up/i;
const plainText = (s) => s.replace(/<[^>]*>/g, ' ').replace(/&nbsp;|\s+/g, ' ').trim();

/**
 * True when the article ends in a call-to-action link: its last <a> has
 * CTA wording and at most a few words of text follow it. TopicalGuidePage
 * then moves the end-of-article Multiplex unit below the FAQ so it never
 * sits directly under the CTA.
 */
export function endsWithCta(html) {
  if (typeof html !== 'string' || !html) return false;
  const lower = html.toLowerCase();
  // Last real <a> tag (not <abbr>, <address>, ...).
  let start = -1;
  for (const m of lower.matchAll(/<a[\s>]/g)) start = m.index;
  if (start === -1) return false;
  const close = lower.indexOf('</a>', start);
  if (close === -1) return false;
  if (plainText(html.slice(close + 4)).length > 40) return false; // prose follows
  return CTA_LINK.test(plainText(html.slice(start, close)));
}
